import ApiManager, {RequestType} from '../ApiManager';
import {
  AnalyticsTranscriptionResponse,
  AnalyticsReactionResponse,
  AnalyticsAttentionResponse,
  ClassesResponse,
  ClassMetadataResponse,
  CreateClassesRequest,
  StartConferenceResponse,
  GetRecordingResponse,
  StudentType,
} from './ClassApiModels';

export enum DeleteTypeId {
  sessionId,
  scheduleId,
}

export interface IClassApiManager {
  getClassesMetadata(): Promise<ClassMetadataResponse>;
  createClass(classObject: CreateClassesRequest): Promise<ClassesResponse>;
  getClasses(minDate: string, maxDate: string): Promise<ClassesResponse[]>;
  startConference(id: string): Promise<StartConferenceResponse>;
  sendReaction(classId: string, reaction: string): Promise<void>;
  getTranscriptionAnalytics(
    classId: string,
  ): Promise<AnalyticsTranscriptionResponse>;
  getReactionAnalytics(classId: string): Promise<AnalyticsReactionResponse>;
  getAttentionAnalytics(classId: string): Promise<AnalyticsAttentionResponse>;
  getRecording(classId: string): Promise<GetRecordingResponse>;
  deleteLesson(idToDelete: string, deleteTypeId: DeleteTypeId): Promise<void>;
  getAttendees(classId: string): Promise<StudentType[]>;
}

export default class ClassApiManager implements IClassApiManager {
  startConference = (id: string): Promise<StartConferenceResponse> => {
    return ApiManager.request<StartConferenceResponse>(
      RequestType.startConference,
      {
        body: {classId: id},
      },
    );
  };

  getClassesMetadata = (): Promise<ClassMetadataResponse> => {
    return ApiManager.request<ClassMetadataResponse>(
      RequestType.classesMetadata,
      {},
    );
  };

  createClass = (
    classObject: CreateClassesRequest,
  ): Promise<ClassesResponse> => {
    return ApiManager.request<ClassesResponse>(RequestType.createClass, {
      body: {
        ...classObject,
      },
    });
  };

  getClasses = async (
    minDate: string,
    maxDate: string,
  ): Promise<ClassesResponse[]> => {
    const response = await ApiManager.request<ClassesResponse[]>(
      RequestType.classes,
      {
        urlParams: {
          startDate: minDate,
          endDate: maxDate,
        },
      },
    );

    return response;
  };

  sendReaction = (classId: string, reaction: string): Promise<void> => {
    return ApiManager.request<void>(RequestType.sendReaction, {
      body: {
        classId,
        reaction,
      },
    });
  };

  getTranscriptionAnalytics = (
    classId: string,
  ): Promise<AnalyticsTranscriptionResponse> => {
    return ApiManager.request<AnalyticsTranscriptionResponse>(
      RequestType.analyticsTranscription,
      {
        query: classId,
      },
    );
  };

  getReactionAnalytics = (
    classId: string,
  ): Promise<AnalyticsReactionResponse> => {
    return ApiManager.request<AnalyticsReactionResponse>(
      RequestType.analyticsReaction,
      {
        query: classId,
      },
    );
  };

  getAttentionAnalytics = (
    classId: string,
  ): Promise<AnalyticsAttentionResponse> => {
    return ApiManager.request<AnalyticsAttentionResponse>(
      RequestType.analyticsAttention,
      {
        query: classId,
      },
    );
  };

  getRecording = (classId: string) => {
    return ApiManager.request<GetRecordingResponse>(RequestType.getRecording, {
      query: classId,
    });
  };

  deleteLesson = async (idToDelete: string, deleteTypeId: DeleteTypeId) => {
    const urlParams =
      deleteTypeId === DeleteTypeId.scheduleId
        ? {
            scheduleId: idToDelete,
          }
        : {
            sessionId: idToDelete,
          };

    return ApiManager.request<void>(RequestType.deleteSchedule, {
      urlParams,
    });
  };

  getAttendees = async (classId: string) => {
    return ApiManager.request<StudentType[]>(RequestType.getAttendees, {
      query: classId,
    });
  };
}
